import { Component, Host, Prop, h, Event, EventEmitter, Method, State, Watch, Element } from '@stencil/core';

// La distanza minima tra i due slider, per evitare che si sovrappongano.
const MIN_GAP = 0;

@Component({
  tag: 'jump-filter-range',
  styleUrl: 'jump-filter-range.scss',
  shadow: true,
})
export class JumpFilterRange {
  @Element() host: HTMLElement;

  /** Nome identificativo del filtro, utilizzato nell'evento finale. */
  @Prop() name: string = 'range-filter';

  /** Testo da mostrare prima del valore minimo. */
  @Prop() labelMinBefore: string = 'Min:';
  /** Testo da mostrare dopo il valore minimo (es. unità di misura). */
  @Prop() labelMinAfter: string = '';

  /** Testo da mostrare prima del valore massimo. */
  @Prop() labelMaxBefore: string = 'Max:';
  /** Testo da mostrare dopo il valore massimo (es. unità di misura). */
  @Prop() labelMaxAfter: string = '';

  /** Il valore minimo possibile per lo slider. */
  @Prop() min: number = 0;
  /** Il valore massimo possibile per lo slider. */
  @Prop() max: number = 100;
  /** L'incremento tra i valori dello slider. */
  @Prop() step: number = 1;

  /** Il valore minimo dello slider. */
  @Prop() minVal: number;
  /** Il valore massimo dello slider. */
  @Prop() maxVal: number;

  /** Stato disabilitato dello slider. */
  @Prop({ reflect: true }) disabled: boolean = false;

  @State() private internalMinVal: number;
  @State() private internalMaxVal: number;

  /**
   * Evento emesso SOLO quando l'utente ha terminato la selezione (al rilascio del mouse).
   */
  @Event({ eventName: 'jump-filterchange' }) filterChange: EventEmitter<{ name: string; values: number[] }>;

  @Watch('minVal')
  watchMinVal(newValue: number) {
    this.internalMinVal = this.validateValue(newValue, 'min');
  }

  @Watch('maxVal')
  watchMaxVal(newValue: number) {
    this.internalMaxVal = this.validateValue(newValue, 'max');
  }

  componentWillLoad() {
    this.initializeValues();
  }

  private initializeValues() {
    this.internalMinVal = this.validateValue(this.minVal, 'min');
    this.internalMaxVal = this.validateValue(this.maxVal, 'max');

    // Assicura che il valore minimo non superi il massimo
    if (this.internalMinVal > this.internalMaxVal) {
      this.internalMinVal = this.internalMaxVal;
    }

    // Sincronizza le prop se erano indefinite
    this.minVal = this.internalMinVal;
    this.maxVal = this.internalMaxVal;
  }

  private validateValue(val: number, type: 'min' | 'max'): number {
    const defaultVal = type === 'min' ? this.min : this.max;
    if (val === undefined || val === null) return defaultVal;
    if (val < this.min) return this.min;
    if (val > this.max) return this.max;
    return val;
  }

  private handleMinInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    let newMinVal = parseInt(input.value, 10);

    if (newMinVal > this.internalMaxVal - MIN_GAP) {
      newMinVal = this.internalMaxVal - MIN_GAP;
      input.value = newMinVal.toString();
    }
    this.internalMinVal = newMinVal;
  };

  private handleMaxInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    let newMaxVal = parseInt(input.value, 10);

    if (newMaxVal < this.internalMinVal + MIN_GAP) {
      newMaxVal = this.internalMinVal + MIN_GAP;
      input.value = newMaxVal.toString();
    }
    this.internalMaxVal = newMaxVal;
  };

  private handleCommit = () => {
    // Sincronizza le prop con lo stato interno prima di emettere l'evento
    this.minVal = this.internalMinVal;
    this.maxVal = this.internalMaxVal;

    this.filterChange.emit({
      name: this.name,
      values: [this.minVal, this.maxVal],
    });
  };

  private getFillStyle() {
    const range = this.max - this.min;
    if (range === 0) return { left: '0%', width: '0%' };

    const leftPercent = ((this.internalMinVal - this.min) / range) * 100;
    const rightPercent = ((this.internalMaxVal - this.min) / range) * 100;
    return { left: `${leftPercent}%`, width: `${rightPercent - leftPercent}%` };
  }

  @Method()
  async getValues(): Promise<number[]> {
    return [this.internalMinVal, this.internalMaxVal];
  }

  render() {
    return (
      <Host class={{ 'is-disabled': this.disabled }}>
        <div class="label-container">
          <label class="label-min">
            {this.labelMinBefore} {this.internalMinVal}{this.labelMinAfter}
          </label>
          <label class="label-max">
            {this.labelMaxBefore} {this.internalMaxVal}{this.labelMaxAfter}
          </label>
        </div>
        <div class="slider-container">
          <div class="slider-track"></div>
          <div class="slider-fill" style={this.getFillStyle()}></div>
          <input
            type="range"
            min={this.min}
            max={this.max}
            step={this.step}
            value={this.internalMinVal}
            onInput={this.handleMinInput}
            onChange={this.handleCommit}
            disabled={this.disabled}
            class="range-input"
            aria-label={this.labelMinBefore}
          />
          <input
            type="range"
            min={this.min}
            max={this.max}
            step={this.step}
            value={this.internalMaxVal}
            onInput={this.handleMaxInput}
            onChange={this.handleCommit}
            disabled={this.disabled}
            class="range-input"
            aria-label={this.labelMaxBefore}
          />
        </div>
      </Host>
    );
  }
}